import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Paper,
  Chip,
  ListItem,
  Modal,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../../Loader";
import Pagination from "../../../../ReusuableComponent/Pagination/Pagination";
import styles from "./InterviewFeedBackList.module.css";
import { AxiosInstance } from "../../../../../axios";

const InterviewFeedBackList = () => {
  const location = useLocation();
  const batchId = location.state?.BATCHID;
  const navigate = useNavigate();
  const txtBSearch = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [updateChipData, setUpdateChipData] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [showModal, setShowModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "500px", md: "600px" },
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    p: 0,
  };

  // Fetch data once
  const fetchFeedbackList = async () => {
    setIsLoading(true);
    try {
      const response = await AxiosInstance.post(
        `api/mockinterview/interview/list_feedback`,
        {
          user_id: "",
          batch_id: [batchId],
          category: "",
          page_no: "",
          page_size: "",
          start_date: "",
          end_date: "",
          search: "",
        }
      );

      if (response?.data?.status === true) {
        const data = response.data.data || [];

        const formatted = data.map((item,index) => ({
          id: item.std_id,
          user_id: item.user_id,
          serialNumber: index + 1,
          name: item.fullname || "-",
          admission_id: item.admission_id || "-",
          phone_no: item.phone_no || "-",
          email: item.email || "-",
          comment: item.comment || "-",
          category: item.category || "-",
          batch_id: item.batch_id || "-",
          created_dt: item.created_dt || null,
        }));

        setFeedbackList(formatted);
        setCurrentPage(1);
      } else {
        setFeedbackList([]);
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
      setFeedbackList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbackList();
  }, []);

  // FILTER + SORT
  const filteredData = useMemo(() => {
    let data = Array.isArray(feedbackList) ? [...feedbackList] : [];

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      data = data.filter(
        (item) =>
          (item.name || "").toLowerCase().includes(s) ||
          (item.feedback || "").toLowerCase().includes(s) ||
          (item.email || "").toLowerCase().includes(s) ||
          (item.admission_id || "").toLowerCase().includes(s)
      );
    }

    if (sortField) {
      data.sort((a, b) => {
        let A = a[sortField];
        let B = b[sortField];

        if (A === undefined || A === null) A = "";
        if (B === undefined || B === null) B = "";

        if (typeof A === "string") A = A.toLowerCase();
        if (typeof B === "string") B = B.toLowerCase();

        if (A === B) return 0;
        return sortOrder === "asc" ? (A > B ? 1 : -1) : A < B ? 1 : -1;
      });
    }

    return data;
  }, [feedbackList, searchTerm, sortField, sortOrder]);

  // pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  // Ensure current page fits within range if filteredData or rowsPerPage changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Reset to page 1 when rowsPerPage or searchTerm changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, filteredData.length);
    // slice with exact bounds
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  // search handlers
  const applySearch = (searchValue) => {
    const v = (searchValue || "").trim();
    setSearchTerm(v);

    if (v) {
      const exists = updateChipData.some(
        (c) => c.type === "search" && c.value === v
      );
      if (!exists) {
        setUpdateChipData((prev) => [
          ...prev.filter((c) => c.type !== "search"),
          { id: Date.now(), type: "search", name: v, value: v },
        ]);
      }
    } else {
      setUpdateChipData((prev) => prev.filter((c) => c.type !== "search"));
    }
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      applySearch(txtBSearch.current?.value || "");
    }
  };

  const handleSearchFocus = () => {
    applySearch(txtBSearch.current?.value || "");
  };

  const handleDelete = (chip) => () => {
    setUpdateChipData((prev) => prev.filter((c) => c.id !== chip.id));
    if (chip.type === "search") {
      setSearchTerm("");
      if (txtBSearch.current) txtBSearch.current.value = "";
    }
  };

  const handleAllClear = () => {
    setUpdateChipData([]);
    setSearchTerm("");
    if (txtBSearch.current) txtBSearch.current.value = "";
    setSortField("");
    setSortOrder("asc");
    setCurrentPage(1);
  };

  // sort
  const handleSort = (e, field) => {
    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newOrder);
  };

  // pagination controls
  const handlePageCount = (e) => {
    const newVal = parseInt(e.target.value, 10) || 10;
    setRowsPerPage(newVal);
  };

  const handlePrev = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNext = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  const startPage =
    filteredData.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const endPage =
    filteredData.length > 0
      ? Math.min(currentPage * rowsPerPage, filteredData.length)
      : 0;

  return (
    <div
      className={`${styles.CourseInfoSec} ${styles.cm_ListViewForm} container-fluid p-0 BathHeader global_container BathDet`}
    >
      {/* Header */}
      <div className="row">
        <div className="col-md-6 col-12">
          <h2 className={`${styles.titleHeader} titleHeader`}>
            Interview Feedback
          </h2>
        </div>
        <div className={`col-md-6 col-12 ${styles.listBtn}`}>
          <ul className={styles.listButton}>
            <li className={styles.listBtnItems}>
              <Link
                to="#"
                onClick={() => navigate(-1)}
                className="secondaryButton"
              >
                Back to View Interview
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Search */}
      <div className="row">
        <div className="col-lg-4 col-md-4 col-sm-12 col-xs-12 mb-2">
          <div className="input-group">
            <input
              ref={txtBSearch}
              type="text"
              className={styles.Inputs}
              id="SearchInput"
              placeholder="Search by Interview Name"
              onKeyDown={handleSearch}
            />
            <span
              className={styles.input_group_text}
              id="SearchInputBtn"
              onClick={handleSearchFocus}
              style={{ cursor: "pointer" }}
            >
              <span className="material-icons">search</span>
            </span>
          </div>
        </div>
      </div>

      {/* Chips */}
      {updateChipData.length > 0 && (
        <div className="row mt-3">
          <div className="col-md-12 col-sm-12 col-xs-12 paperil_global">
            <Paper
              sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                listStyle: "none",
                p: 0.5,
                m: 0,
              }}
              component="ul"
            >
              {updateChipData.map((data) => (
                <ListItem key={data.id} sx={{ width: "auto", p: 0.5 }}>
                  <Chip
                    label={
                      <span className={styles.BoldTyText}>{data.name}</span>
                    }
                    onDelete={handleDelete(data)}
                  />
                </ListItem>
              ))}
              <ListItem
                className="clearAll_list"
                sx={{ width: "auto", p: 0.5 }}
              >
                <a
                  className={styles.allclear}
                  onClick={handleAllClear}
                  style={{ cursor: "pointer" }}
                >
                  Clear All Filters
                </a>
              </ListItem>
            </Paper>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="row mx-0 mt-2">
        <div className="col-md-12 table-responsive tableMinHeight px-0">
          <table className="table" id="tableListHeader">
            <thead>
              <tr>
                <th>
                  <span
                    className="textHead_th"
                    onClick={(e) => handleSort(e, "serialNumber")}
                  >
                    S.No
                  </span>
                  <span
                    className="textHead_th material-icons align-middle"
                    onClick={(e) => handleSort(e, "serialNumber")}
                  >
                    import_export
                  </span>
                </th>
                <th>
                  <span
                    className="textHead_th"
                    onClick={(e) => handleSort(e, "name")}
                  >
                    Name
                  </span>
                  <span
                    className="textHead_th material-icons align-middle"
                    onClick={(e) => handleSort(e, "name")}
                  >
                    import_export
                  </span>
                </th>
                <th>
                  <span
                    className="textHead_th"
                    onClick={(e) => handleSort(e, "admission_id")}
                  >
                    Admission ID
                  </span>
                  <span
                    className="textHead_th material-icons align-middle"
                    onClick={(e) => handleSort(e, "admission_id")}
                  >
                    import_export
                  </span>
                </th>
                <th className={styles.tableThPadding}>
                  <span
                    className="textHead_th"
                    onClick={(e) => handleSort(e, "phone_no")}
                    
                  >
                    Phone Number
                  </span>
                  <span
                    className="textHead_th material-icons align-middle"
                      onClick={(e) => handleSort(e, "phone_no")}
                  >
                    import_export
                  </span>
                </th>
                <th className={styles.tableThPadding}>
                  <span
                    className="textHead_th"
                    onClick={(e) => handleSort(e, "email")}
                  >
                    Email
                  </span>
                  <span
                    className="textHead_th material-icons align-middle"
                    onClick={(e) => handleSort(e, "email")}
                  >
                    import_export
                  </span>
                </th>
                <th className={styles.tableThPadding}>
                  <span className="textHead_th">Feedback</span>
                </th>
                <th>
                  <span className="textHead_th">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No records to display
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  // Key includes currentPage to avoid DOM reuse across pages
                  <tr key={`${item.id ?? index}-${currentPage}-${index}`}>
                    {/* <td>{(currentPage - 1) * rowsPerPage + index + 1}</td> */}
                    <td>{item.serialNumber}</td>
                    <td>{item.name}</td>
                    <td>{item.admission_id}</td>
                    <td>{item.phone_no}</td>
                    <td>{item.email}</td>
                    <td>
                      <span className={styles.ellipsis} title={item.feedback}>
                        {item.comment}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="view_icon_button"
                        onClick={() => {
                          setSelectedFeedback(item);
                          setShowModal(true);
                        }}
                        title="View Feedback"
                      >
                        <span className="visibleicon material-icons">
                          visibility
                        </span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="row mt-2">
        <Pagination
          rowsPerPage={rowsPerPage}
          handlePageCount={handlePageCount}
          startPage={startPage}
          endPage={endPage}
          totalRecords={filteredData.length}
          handlePrev={handlePrev}
          handleNext={handleNext}
          currentPrevPage={currentPage === 1}
          currentNextPage={currentPage === totalPages}
          prevButton={`${styles.prev_buttonViewEvaluator} ${styles.materialIconsRightAndLeft} me-2`}
        />
      </div>

      {/* Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="view-feedback-modal-title"
        aria-describedby="view-feedback-modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography
              id="view-feedback-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, color: "#041134", fontSize: "18px" }}
            >
              View Feedback
            </Typography>
            <IconButton
              onClick={() => setShowModal(false)}
              sx={{ color: "#666", "&:hover": { color: "#333" } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5, minHeight: "100px" }}>
            <Typography
              sx={{
                color: "#00000080",
                fontSize: "14px",
                fontWeight: 700,
                mb: 1,
              }}
            >
              Comments
            </Typography>
            <Typography
              id="view-feedback-modal-description"
              sx={{ lineHeight: 1.6, fontSize: "16px", fontWeight: 700 }}
            >
              {selectedFeedback?.comment || "No feedback available"}
            </Typography>
          </Box>

          {/* <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 1.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography
              onClick={() => setShowModal(false)}
              sx={{
                color: "#c0272d",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                px: 2,
                py: 1,
                "&:hover": { opacity: 0.8 },
              }}
            >
              Close
            </Typography>
          </Box> */}
        </Box>
      </Modal>

      {/* Loader */}
      {isLoading && (
        <div
          className="loaderApi"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 999,
          }}
        >
          <Loader />
        </div>
      )}
    </div>
  );
};

export default InterviewFeedBackList;
